<template>
    <div class="ask-exchange" data-testid="ask-box-exchange">
        <p
            class="ask-exchange__question"
            data-testid="ask-box-exchange-question"
        >
            {{ question }}
        </p>

        <hr class="ask-exchange__rule" />

        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
            class="ask-exchange__answer"
            data-testid="ask-box-exchange-answer"
            v-html="rendered"
        />

        <AskReferences
            v-if="answer.references?.length"
            :references="answer.references"
            @navigate="emit('navigate')"
        />

        <AskThreadOffer
            v-if="answer.offer_thread"
            :offer="answer.offer_thread"
            :question="question"
            @handed-off="emit('handedOff')"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import AskReferences from "@/components/AskBox/AskReferences.vue";
import AskThreadOffer from "@/components/AskBox/AskThreadOffer.vue";
import { renderAnswer } from "@/components/AskBox/ask-content";
import type { AskAnswer } from "@/lib/openapi/gen/types.gen";

/**
 * One finished question and its answer, as it sits above the live exchange.
 *
 * Its own component because a past exchange is not a lesser thing than the
 * current one: the links still go where they went, and a thread offered two
 * questions ago is still an offer worth taking — someone reads the answer,
 * thinks about it, asks one more thing, and only then decides to open the
 * thread. Rendering these as inert text would take the decision away at the
 * moment they were closest to making it.
 *
 * The live exchange keeps its own markup in `AskBox`, because it has two states
 * this one cannot have — thinking, and failed — and folding those in here would
 * mean a component that is mostly branches about which of three things it is.
 */
const props = defineProps<{
    question: string;
    answer: AskAnswer;
}>();

// `handedOff`, spelled as `AskThreadOffer` spells its own — the two sit beside
// each other and Vue accepts either, so the only thing a second spelling buys a
// reader is a moment wondering whether the difference means something.
const emit = defineEmits<{
    navigate: [];
    handedOff: [];
}>();

const rendered = computed(() => renderAnswer(props.answer.answer));
</script>

<style scoped>
/* The whole exchange is dimmed, not just its prose.

   With the fade on the answer alone, a past exchange's entity links and its
   "start a thread" button drew at full strength beside the live one's — so a
   sitting with three refusals in it showed three identical blue buttons and
   nothing said which one answered the question you just asked. Confirmed in
   the browser, not deduced: the buttons are siblings of the answer, not
   children, so they never inherited it.

   Deliberately shallow. These are still live controls — a thread offered two
   questions ago is still worth taking — and anything heavier would read as
   disabled rather than earlier. */
.ask-exchange {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    opacity: 0.75;
}

/* The same muted treatment the live question gets: context, not content, and it
   must not compete with the answer under it. */
.ask-exchange__question {
    margin: 0;
    color: var(--p-text-muted-color);
    font-size: 0.85rem;
}

.ask-exchange__rule {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--p-surface-200);
}

/* Matches the live answer's type exactly; the fade above is the only
   difference between a past answer and the current one. These are still
   readable answers someone may be working from — it says which one is current,
   not that the rest are finished with. */
.ask-exchange__answer {
    font-size: 0.95rem;
    line-height: var(--body-line-height, 1.5);
}

.ask-exchange__answer :deep(p) {
    margin: 0.5em 0;
}

.ask-exchange__answer :deep(p:first-child) {
    margin-top: 0;
}

.ask-exchange__answer :deep(p:last-child) {
    margin-bottom: 0;
}

.ask-exchange__answer :deep(a) {
    color: var(--p-primary-color);
    text-decoration: underline;
    overflow-wrap: anywhere;
}

.ask-exchange__answer :deep(code) {
    background: var(--p-surface-200);
    padding: 0.1em 0.3em;
    font-size: 0.85em;
}

.ask-exchange__answer :deep(ul),
.ask-exchange__answer :deep(ol) {
    margin: 0.5em 0;
    padding-left: 1.4em;
    list-style-position: outside;
}
</style>
